// src/components/Note.js
import React, { useEffect, useState, useMemo } from "react";
import { format, addDays, subDays, addMonths, isBefore } from "date-fns";
import NotesCalendar from "./NotesCalendar";
import { Button, Form } from "react-bootstrap";
import { PencilSquare, Search, Calendar3, Funnel, XCircle, SortAlphaDown, SortAlphaUp } from "react-bootstrap-icons";
import QuickNoteModal from "./QuickNoteModal";
import NoteCard from "./NoteCard";
import { useNotes } from "../services/NotesContext";
import { fetchWithAuth } from "../services/AuthService";
import { useTranslation } from 'react-i18next';
import { COLOR_CATEGORIES } from "../constants/colorCategories";
import "../css/Note.css";

const Note = () => {
  const { t } = useTranslation();
  const { notes, addNote, updateNote, deleteNote, loading, checkAndLoadNotes, refreshNotesHash } = useNotes();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isQuickNoteOpen, setIsQuickNoteOpen] = useState(false);
  const [noteToEdit, setNoteToEdit] = useState(null); // ✅ nouvelle variable d'état
  const [searchQuery, setSearchQuery] = useState("");
  const [activePanel, setActivePanel] = useState(null);
  const [activeCategoryFilters, setActiveCategoryFilters] = useState([]);
  const [reverseOrder, setReverseOrder] = useState(false);
  const searchInputRef = React.useRef(null);
  const containerRef = React.useRef(null);

useEffect(() => {
  let mounted = true;
  if (mounted) checkAndLoadNotes();
  return () => { mounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

useEffect(() => {
  if (activePanel === "search") {
    searchInputRef.current?.focus();
  }
}, [activePanel]);

useEffect(() => {
  if (activePanel && containerRef.current) {
    const top = containerRef.current.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: Math.max(top - 70, 0), behavior: 'smooth' }); // 70 = hauteur de ton header
  }
}, [activePanel]);
// useEffect(() => {
//   if (activePanel) {
//     const container = document.querySelector('.journal-container');
//     if (container) {
//       // scroll pour que le conteneur soit en haut de la page
//       container.scrollIntoView({ behavior: 'smooth', block: 'start' });
//     }
//   }
// }, [activePanel]);

  // --- Filtrage des notes par date ---
  const notesByDate = useMemo(() => {
    let sortedNotes = notes
      .filter(
        (n) =>
          isBefore(new Date(n.createdAt), selectedDate) ||
          new Date(n.createdAt).toDateString() === selectedDate.toDateString()
      )
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    if (reverseOrder) {
      sortedNotes = sortedNotes.slice().reverse();
    }

    return sortedNotes;
  }, [notes, selectedDate, reverseOrder]);

  
  // --- Filtrage combiné (recherche + catégorie) ---
  const filteredNotes = useMemo(() => {
    let filtered = notesByDate;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((note) =>
        (note.text && note.text.toLowerCase().includes(query)) ||
      (note.title && note.title.toLowerCase().includes(query))
    );
  }
  if (activeCategoryFilters.length > 0) {
    filtered = filtered.filter((note) => activeCategoryFilters.includes(note.categoryId));
  }
  return filtered;
}, [notesByDate, searchQuery, activeCategoryFilters]);

const notesCountByCategory = useMemo(() => {
  const counts = {};
  filteredNotes.forEach(note => {
    counts[note.categoryId] = (counts[note.categoryId] || 0) + 1;
  });
  return counts;
}, [filteredNotes]);

// --- Données pour calendrier ---
const notesHeader = useMemo(() => {
  return notes.reduce((acc, note) => {
    const dateKey = format(new Date(note.createdAt), "dd/MM/yyyy");
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(note);
      return acc;
    }, {});
  }, [notes]);

  // --- Ajouter ou Modifier une note ---
  const handleSaveNote = async (note) => {
    if (noteToEdit) {
      updateNote({ ...noteToEdit, ...note });
    } else {
      addNote(note);
    }
    await refreshNotesHash();

    setIsQuickNoteOpen(false);
    setNoteToEdit(null);
  };

  // --- Supprimer une note ---
  const handleDeleteNote = async (id) => {
    try {
      // ✅ Appel backend pour suppression
      const res = await fetchWithAuth(`/api/notes/${id}`, {
        method: "DELETE",
      });

      if (!res.ok) {
        throw new Error("Erreur lors de la suppression de la note");
      }

      deleteNote(id);
      await refreshNotesHash();

    } catch (error) {
      console.error("Erreur de suppression :", error);
      alert("Impossible de supprimer la note. Veuillez réessayer.");
    }
  };

  return (
    <div className="journal-container" ref={containerRef}>
      {/* --- Barre d’action --- */}
      <div
        className="note-toolbar"
        style={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          padding: "0.5rem 0",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          background: "#2b2f38",
          borderRadius: "12px",
          marginBottom: "0.8rem",
          gap: "0.8rem",
        }}
      >
        {/* Recherche */}
        <Button
          variant="link"
          onClick={() =>
            setActivePanel(activePanel === "search" ? null : "search")
          }
        >
          <Search
            size={22}
            color={
              activePanel === "search" || searchQuery
                ? "#6F3F89"
                : "#bbb"
            }
          />
        </Button>

        {/* Calendrier */}
        <Button
          variant="link"
          onClick={() =>
            setActivePanel(activePanel === "calendar" ? null : "calendar")
          }
        >
          <Calendar3
            size={22}
            color={
              activePanel === "calendar" ||
              selectedDate.toDateString() !== new Date().toDateString()
                ? "#6F3F89"
                : "#bbb"
            }
          />
        </Button>

        {/* Filtres de catégorie */}
        <Button
          variant="link"
          onClick={() =>
            setActivePanel(activePanel === "filter" ? null : "filter")
          }
        >
          <Funnel
            size={22}
            color={
              activePanel === "filter" || activeCategoryFilters.length > 0
                ? "#6F3F89"
                : "#bbb"
            }
          />
        </Button>

        {/* Filtres de tri chronologique */}
        <Button
          variant="link"
          onClick={() => setReverseOrder(prev => !prev)}
        >
          {reverseOrder ? (
            <SortAlphaUp size={22} color="#6F3F89" />
          ) : (
            <SortAlphaDown size={22} color="#bbb" />
          )}
        </Button>

        {/* Bouton reset général */}
        <Button
          variant="link"
          onClick={() => {
            setSearchQuery("");
            setSelectedDate(new Date());
            setActiveCategoryFilters([]);
            setReverseOrder(false);
            setActivePanel(null);
          }}
        >
          <XCircle
            size={22}
            color={
              searchQuery ||
              activeCategoryFilters.length > 0 ||
              selectedDate.toDateString() !== new Date().toDateString()
                ? "#6F3F89"
                : "#bbb"
            }
          />
        </Button>

        {/* Ajout rapide */}
        <Button
          variant="link"
          className="quick-note-add-btn"
          onClick={() => {
            setNoteToEdit(null);
            setIsQuickNoteOpen(true);
          }}
        >
          <PencilSquare size={30} color="#A3A3A3" />
        </Button>
      </div>

      {/* --- Panneaux dynamiques --- */}
      <div className="note-active-panel" style={{ marginBottom: "1rem" }}>
        {activePanel === "search" && (
          <div
            className="note-search-bar"
            style={{
              display: "flex",
              alignItems: "center",
              margin: "auto",
              marginBottom: "1rem",
              background: "#2b2f38",
              borderRadius: "12px",
              padding: "0.5rem 0.8rem",
              minWidth: "335px",
              maxWidth: "50%"
            }}
          >
            <Form.Control
              ref={searchInputRef}
              type="text"
              placeholder={t(`note.filters.search`)}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                flex: 1,
                background: "#f5f5f5",
                border: "none",
                color: "#333",
                borderRadius: "8px",
                padding: "0.4rem 2rem 0.4rem 0.6rem", // espace pour l’icône
                position: "relative",
                margin:"auto"
              }}
            />

            {/* Bouton pour effacer la recherche */}
            {searchQuery && (
              <Button
                variant="link"
                onClick={() => setSearchQuery("")}
                style={{
                  position: "relative",
                  marginLeft: "-2rem", // chevauche le champ
                  zIndex: 2,
                  color: "#6F3F89",
                  padding: 0,
                }}
                title="Effacer"
              >
                <XCircle size={20} />
              </Button>
            )}
          </div>
        )}

        {activePanel === "calendar" && (
          <div
            className="note-search-bar"
            style={{
              display: "flex",
              alignItems: "center",
              margin: "auto",
              marginBottom: "1rem",
              background: "#2b2f38",
              borderRadius: "12px",
              minWidth: "350px",
              maxWidth: "50%",
              height: "360px"
            }}
          >
          <NotesCalendar
            selectedDate={selectedDate}
            notes={notesHeader}
            onSelectDate={(date) => {
              setSelectedDate(date);
              setActivePanel(null); // ferme le calendrier quand on clique sur une date
            }}
            onMonthChange={(direction) =>
              setSelectedDate((prev) => addMonths(prev, direction))
            }
            onPrevDay={() => setSelectedDate((prev) => subDays(prev, 1))}
            onNextDay={() => setSelectedDate((prev) => addDays(prev, 1))}
            colorCategories={COLOR_CATEGORIES}
          />
        </div>  
        )}

        {activePanel === "filter" && (
          <div
            className="note-filter-panel"
            style={{
              background: "#2b2f38",
              borderRadius: "12px",
              padding: "1rem",
              display: "flex",
              justifyContent: "space-around",
              flexWrap: "wrap",
              maxWidth: "450px",
              gap: "0.5rem",
              margin:"auto"
            }}
          >
          {COLOR_CATEGORIES.map((cat) => {
            const isActive = activeCategoryFilters.includes(cat.id);
            const count = notesCountByCategory[cat.id] || 0;

            return (
              <div
                key={cat.id}
                onClick={() => {
                  setActiveCategoryFilters((prev) =>
                    prev.includes(cat.id)
                      ? prev.filter((id) => id !== cat.id)
                      : [...prev, cat.id]
                  );
                }}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  cursor: "pointer",
                  gap: "0.0rem",
                  width: "28px",
                }}
                title={t(`note.input.${cat.id}`)}
              >
                <div
                  style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    backgroundColor: cat.color,
                    border: isActive ? "3px solid #fff" : "2px solid #555",
                    transition: "transform 0.2s ease",
                    transform: isActive ? "scale(1.1)" : "scale(1)",
                  }}
                />
                <span
                  style={{
                    color: "#ddd",
                    fontSize: "0.8rem",
                    fontWeight: "500",
                    textAlign: "center",
                    minHeight: "1rem",
                  }}
                >
                  {count > 0 ? count : ""}
                </span>
              </div>
            );
          })}
          </div>
        )}
      </div>

      {/* --- Modale pour ajouter / modifier une note --- */}
      <QuickNoteModal
        show={isQuickNoteOpen}
        onClose={() => {
          setIsQuickNoteOpen(false);
          setNoteToEdit(null);
        }}
        onSave={handleSaveNote}
        scanResultId={null}
        presetCategory={noteToEdit ? noteToEdit.categoryId : null}
        existingNote={noteToEdit} // ✅ on passe la note à modifier
      />

      {/* --- Liste des notes --- */}
      {loading ? (
        <p className="loading-text">Chargement des notes...</p>
      ) : (
        <div className="notes-list">
          {filteredNotes.map((note) => (
            <NoteCard
              key={note._id}
              note={note}
              highlightQuery={searchQuery}
              colorCategories={COLOR_CATEGORIES}
              onEdit={(note) => {
                setNoteToEdit(note);
                setIsQuickNoteOpen(true);
              }}
              onDelete={handleDeleteNote}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Note;
